import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useFetch, NeedTenant } from "../hooks/useData";
import { GlassCard, Loading, SectionTitle, Badge } from "../components/common";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import client from "../lib/api";
import { Bell, Activity, AlertTriangle, ShieldAlert, TrendingUp, Check, RadioTower, Loader2, Save } from "lucide-react";
import { toast } from "sonner";

const TYPE_ICON = { "SLA Breach": AlertTriangle, "Process Drift": TrendingUp, Anomaly: Activity, Compliance: ShieldAlert };

function MonitoringPanel({ onScan }) {
  const { can } = useAuth();
  const { data, loading, reload } = useFetch("/monitoring/settings");
  const [s, setS] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (data?.settings) setS(data.settings); }, [data]);
  if (loading || !s) return null;

  const save = async () => {
    setSaving(true);
    try { await client.put("/monitoring/settings", { enabled: s.enabled, interval_minutes: Number(s.interval_minutes), conformance_target: Number(s.conformance_target), sla_hours: Number(s.sla_hours) }); toast.success("Monitoring settings saved"); await reload(); }
    catch { toast.error("Save failed"); } finally { setSaving(false); }
  };
  const scan = async () => {
    setScanning(true);
    try { const { data } = await client.post("/monitoring/scan"); toast.success(`Scan complete — ${data.new_alerts} new alert(s)`); await onScan?.(); }
    catch { toast.error("Scan failed"); } finally { setScanning(false); }
  };

  const num = (key, label, suffix) => (
    <div>
      <label className="text-xs font-medium text-slate-500">{label}</label>
      <div className="relative mt-1">
        <Input type="number" value={s[key]} disabled={!can("admin")} onChange={(e) => setS({ ...s, [key]: e.target.value })} data-testid={`mon-${key}`} className="bg-white/70 rounded-xl h-9 pr-10" />
        <span className="absolute right-3 top-1.5 text-xs text-slate-400">{suffix}</span>
      </div>
    </div>
  );

  return (
    <GlassCard className="p-6">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-2"><div className="w-9 h-9 rounded-xl pm-gradient flex items-center justify-center"><RadioTower className="w-4 h-4 text-white" /></div>
          <div><h3 className="font-bold text-slate-900">Continuous monitoring</h3><p className="text-xs text-slate-500">Scheduled re-mining raises SLA, drift & anomaly alerts automatically{data.settings.last_scan ? ` · last scan ${new Date(data.settings.last_scan).toLocaleTimeString()}` : ""}</p></div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-xs font-semibold ${s.enabled ? "text-emerald-600" : "text-slate-400"}`}>{s.enabled ? "Active" : "Paused"}</span>
          <Switch checked={!!s.enabled} disabled={!can("admin")} onCheckedChange={(v) => setS({ ...s, enabled: v })} data-testid="mon-enabled" />
          {can("analyst") && <Button onClick={scan} disabled={scanning} data-testid="run-scan" variant="outline" className="rounded-xl h-9">{scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RadioTower className="w-4 h-4 mr-1.5" />Run scan now</>}</Button>}
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        {num("interval_minutes", "Scan interval", "min")}
        {num("conformance_target", "Conformance target", "%")}
        {num("sla_hours", "Cycle-time SLA", "hrs")}
      </div>
      {can("admin") && <Button onClick={save} disabled={saving} data-testid="save-monitoring" className="mt-4 rounded-xl pm-gradient text-white border-0 h-10">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" />Save settings</>}</Button>}
    </GlassCard>
  );
}

function Inner() {
  const { can } = useAuth();
  const { data, loading, reload } = useFetch("/alerts");
  if (loading) return <Loading />;
  const alerts = data || [];
  const open = alerts.filter((a) => a.status === "open");
  const byType = (t) => alerts.filter((a) => a.type === t).length;

  const act = async (id, status) => {
    try { await client.patch(`/alerts/${id}`, { status }); await reload(); toast.success(`Alert ${status}`); }
    catch { toast.error("Update failed"); }
  };

  return (
    <div className="space-y-5 pm-fade-up">
      <SectionTitle eyebrow="Continuous Monitoring" title="Monitoring & Alerts" sub="Drift detection, anomalies, SLA and compliance signals" />
      <MonitoringPanel onScan={reload} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        {[["SLA Breach", "from-red-500 to-rose-500"], ["Process Drift", "from-amber-500 to-orange-500"], ["Anomaly", "from-cyan-500 to-blue-500"], ["Compliance", "from-violet-500 to-purple-500"]].map(([t, g]) => {
          const Icon = TYPE_ICON[t];
          return (
            <GlassCard key={t} className="p-5">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${g} flex items-center justify-center mb-3`}><Icon className="w-5 h-5 text-white" /></div>
              <div className="text-2xl font-bold text-slate-900 font-display">{byType(t)}</div>
              <div className="text-xs text-slate-500">{t}</div>
            </GlassCard>
          );
        })}
      </div>
      <GlassCard className="p-6">
        <div className="flex items-center gap-2 mb-4"><Bell className="w-4 h-4 text-red-500" /><h3 className="font-bold text-slate-900">Alert stream</h3><span className="ml-auto text-xs bg-red-100 text-red-600 font-semibold px-2 py-0.5 rounded-full">{open.length} open</span></div>
        <div className="space-y-3">
          {alerts.map((a) => {
            const Icon = TYPE_ICON[a.type] || Bell;
            return (
              <div key={a.id} className="flex items-start gap-4 p-4 rounded-xl bg-white/60 border border-white/60" data-testid={`monitor-alert-${a.id}`}>
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${a.severity === "high" ? "bg-red-100 text-red-600" : a.severity === "medium" ? "bg-amber-100 text-amber-600" : "bg-sky-100 text-sky-600"}`}><Icon className="w-4 h-4" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap"><span className="font-semibold text-slate-800">{a.title}</span><Badge kind="sev" value={a.severity} /><Badge value={a.status} /></div>
                  <p className="text-sm text-slate-500 mt-1">{a.message}</p>
                </div>
                {can("analyst") && a.status !== "resolved" && (
                  <div className="flex gap-2 shrink-0">
                    {a.status === "open" && <Button onClick={() => act(a.id, "acknowledged")} data-testid={`ack-${a.id}`} variant="outline" size="sm" className="rounded-lg">Acknowledge</Button>}
                    <Button onClick={() => act(a.id, "resolved")} data-testid={`resolve-${a.id}`} size="sm" className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white border-0"><Check className="w-3.5 h-3.5 mr-1" />Resolve</Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </GlassCard>
    </div>
  );
}

export default function Monitoring() { return <NeedTenant><Inner /></NeedTenant>; }
